import React, { useCallback, useEffect, useState } from 'react';
import first from 'lodash/first';
import find from 'lodash/find';
import mapValues from 'lodash/mapValues';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import {
  ProductsAiSearchFileAttachment,
  ProductsAiSearchOnClipGlobalSearch
} from '../../../../ProductsAiSearch.types';

import {
  FETCH_IMAGE_CROPS_QUERY,
  FetchImageCropsQueryResponse
} from '../../../../../../../../imageCrops/queries/fetchImageCrops.query';

import { useImageCrops } from '../../../../../../../../imageCrops/hooks/useImageCrops';

import { PureButtonHelper } from '../../../../../../../../../helpers/buttons/PureButtonHelper';
import { LoadingSkeleton } from '../../../../../../../../../helpers/LoadingSkeleton';
import { AlertMessage } from '../../../../../../../../../helpers/AlertMessage';
import {
  DropzoneHelper,
  DropzoneHelperFileIds,
  DropzoneHelperFiles
} from '../../../../../../../../../helpers/dropzone/DropzoneHelper';

import { words } from '../../../../../../../../../locales/keys';

import { ImageCropCache } from '../../../../../../../../imageCrops/ImageCropCache';

interface ProductsAiSearchByLSGlobalModalButtonContentProps {
  initialImageCrop?: ProductsAiSearchFileAttachment;
  onSubmit: ProductsAiSearchOnClipGlobalSearch;
  hideModal: () => void;
}

function ProductsAiSearchByLSGlobalModalButtonContent({
  initialImageCrop,
  onSubmit,
  hideModal
}: ProductsAiSearchByLSGlobalModalButtonContentProps) {
  const [uploadedFileIds, setUploadedFileIds] = useState<DropzoneHelperFileIds>(
    initialImageCrop ? [initialImageCrop.id] : []
  );

  const imageCropId = first(uploadedFileIds);

  const {
    imageCrops,
    imageCropsError,
    imageCropsFetched,
    imageCropsFilters,
    filterImageCrops
  } = useImageCrops<FetchImageCropsQueryResponse>({
    cacheKey: ImageCropCache.imageCropDropzoneHelper(),
    query: FETCH_IMAGE_CROPS_QUERY,
    initialFilters: {
      id: {
        in: uploadedFileIds || []
      }
    },
    initialLimit: 1,
    options: {
      enabled: !isEmpty(uploadedFileIds),
      cacheTime: 0,
      enabledPlaceholder: false
    }
  });

  const imageCrop = find(imageCrops, ['id', imageCropId]);

  useEffect(() => {
    if (!isEqual(imageCropsFilters.id?.in, uploadedFileIds)) {
      filterImageCrops({
        id: { in: uploadedFileIds }
      });
    }
  }, [uploadedFileIds, imageCropsFilters, filterImageCrops]);

  const initialFiles: DropzoneHelperFiles = isEmpty(uploadedFileIds)
    ? {}
    : mapValues(keyBy(imageCrops, 'uuid'), (attachment) => ({
        activeTab: 'general',
        id: attachment.uuid,
        name: attachment.name,
        file: attachment.file,
        size: attachment.size,
        progresses: { 0: 100 },
        state: 'finished' as const,
        uploadedId: attachment.id
      }));

  const handleSubmit = useCallback<() => void>(() => {
    onSubmit({ fileAttachment: imageCrop });
    hideModal();
  }, [imageCrop, hideModal, onSubmit]);

  return (
    <>
      <div className="p-4">
        <AlertMessage message={imageCropsError} />
        <LoadingSkeleton loaded={isEmpty(uploadedFileIds) || imageCropsFetched}>
          <DropzoneHelper
            type="imageCrops"
            accept={['.jpg', '.jpeg', '.png']}
            withoutTabs
            maxFiles={1}
            preventMaxFilesOverload
            initialFileIds={uploadedFileIds}
            initialFiles={initialFiles}
            onChange={setUploadedFileIds}
            withoutDownload
          />
        </LoadingSkeleton>
      </div>

      <div className="flex p-4 space-x-1 justify-between">
        <div className="flex space-x-1" />

        <div className="flex space-x-1">
          <PureButtonHelper
            className="py-2 pl-4 pr-4 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-6 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0"
            i18nText={words.cancel}
            onClick={hideModal}
          />

          <PureButtonHelper
            className="py-2 pl-4 pr-4 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-6 focus:ring-base text-white bg-blue-500 hover:bg-blue-600 shadow-sm hover:shadow-md"
            i18nText={words.search}
            disabled={!imageCrop}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </>
  );
}

export default ProductsAiSearchByLSGlobalModalButtonContent;
